import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { LayoutDashboard, Receipt, FileText, CalendarDays, Boxes, Wrench, Settings, LogOut, Menu, Plus, History } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { fileUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Resumen", icon: LayoutDashboard, id: "dashboard" },
  { to: "/ventas", label: "Notas de venta", icon: Receipt, id: "sales" },
  { to: "/reportes", label: "Historial de ventas", icon: History, id: "reports" },
  { to: "/cotizaciones", label: "Cotizaciones", icon: FileText, id: "quotes" },
  { to: "/agenda", label: "Agenda de trabajos", icon: CalendarDays, id: "jobs" },
  { to: "/materiales", label: "Materiales", icon: Boxes, id: "materials" },
  { to: "/herramientas", label: "Herramientas", icon: Wrench, id: "tools" },
  { to: "/configuracion", label: "Configuración", icon: Settings, id: "settings" },
];

function Brand() {
  const { settings } = useApp();
  return (
    <div className="flex items-center gap-3 px-5 py-6">
      {settings?.logo_path ? (
        <img src={fileUrl(settings.logo_path)} alt="logo" className="h-10 w-10 rounded-md object-contain bg-white p-0.5" />
      ) : (
        <div className="h-10 w-10 rounded-md bg-amber-600 grid place-items-center font-display font-extrabold text-white">V</div>
      )}
      <div className="leading-tight">
        <p className="font-display font-bold text-stone-50 text-sm tracking-tight">{settings?.name || "VICON CARPINTERIA"}</p>
        <p className="text-[11px] uppercase tracking-[0.2em] text-amber-500/80">Taller</p>
      </div>
    </div>
  );
}

function SideNav({ onNavigate }) {
  const { logout } = useApp();
  return (
    <div className="flex h-full flex-col bg-[#1E1B18] grain">
      <Brand />
      <nav className="flex-1 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon, id }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            onClick={onNavigate}
            data-testid={`nav-${id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                isActive ? "bg-[#2A2421] text-amber-400 font-semibold" : "text-stone-300 hover:bg-[#2A2421] hover:text-stone-50"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <button
        onClick={logout}
        data-testid="logout-button"
        className="m-3 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-stone-400 hover:text-red-300 hover:bg-[#2A2421] transition-colors"
      >
        <LogOut className="h-4 w-4" /> Cerrar sesión
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64">
        <SideNav />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 border-0">
          <SideNav onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b bg-[#FDFBF7]/95 backdrop-blur-md px-4 sm:px-8 h-16">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden lg:block text-sm text-stone-500">Sistema de gestión del taller</div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm" data-testid="topbar-new-quote">
              <Link to="/cotizaciones/nueva"><Plus className="h-4 w-4 mr-1" />Cotización</Link>
            </Button>
            <Button asChild size="sm" className="bg-amber-700 hover:bg-amber-800" data-testid="topbar-new-sale">
              <Link to="/ventas/nueva"><Plus className="h-4 w-4 mr-1" />Nota de venta</Link>
            </Button>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-[1400px]">{children}</main>
      </div>
    </div>
  );
}
